import { differenceInCalendarDays, parseISO } from 'date-fns';
import { useCallback } from 'react';

import type { SimilarItemAlert } from '@/redux/modules/similarItemAlerts/similarItemAlerts.types';

import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { getItemSummaryFirstImageUrl, getItemSummaryTitle } from '@/redux/modules/item/summary/itemSummary.selectors';
import { openSimilarItemAlertDeleteModal } from '@/redux/modules/modal';
import { shortFormatDate } from '@/utils/dates';
import { useAppFormatMessage } from '@/utils/intl/useAppFormatMessage';

// savedsearch-api returns the Go zero-value time (`0001-01-01T00:00:00Z`) for a lot that has never
// triggered an email — match it by year, not an exact string, since the wire format carries a `Z`
// and parses to "Dec 31, 0001" after the tz offset. Anything at year 1 or earlier is "never sent".
const hasNeverSent = (lastSent: string) => {
    if (!lastSent) {
        return true;
    }
    const parsed = parseISO(lastSent);
    return Number.isNaN(parsed.getTime()) || parsed.getUTCFullYear() <= 1;
};

/**
 * The title fallback chain, kept separate from the hook so the For You tab's keyword filter can
 * apply the exact same one without subscribing to redux per alert. If the filter and the row
 * disagree, a bidder searches for what the row plainly shows and gets nothing back.
 */
export const resolveSimilarItemAlertTitle = ({
    hydratedTitle,
    lotTitle,
    untitledLot,
}: {
    hydratedTitle: string;
    lotTitle: string;
    untitledLot: string;
}) => hydratedTitle || lotTitle || untitledLot;

/**
 * Everything a Similar Item Alert row renders, derived once for both management surfaces — the
 * Emails & Notifications section and the For You tab. The two lay their columns out differently but
 * show the same lot, so keeping the title fallback, the never-sent rule and the delete dispatch here
 * stops the surfaces drifting apart.
 *
 * The lot's title and thumbnail come from the shared item-summary slice, which the surface hydrates;
 * a lot item-api can't resolve falls back to the title savedsearch-api stored on the row, so a gap
 * there never blanks a row. Rows saved before the backend persisted titles have neither, and the
 * lot id stands in — without it the For You row would be a link with no accessible name.
 */
export const useSimilarItemAlertRow = (alert: SimilarItemAlert) => {
    const dispatch = useAppDispatch();
    const formatMessage = useAppFormatMessage();

    const hydratedTitle = useAppSelector((state) => getItemSummaryTitle(state, alert.lotId));
    const imageUrl = useAppSelector((state) => getItemSummaryFirstImageUrl(state, alert.lotId));

    const title = resolveSimilarItemAlertTitle({
        hydratedTitle,
        lotTitle: alert.lotTitle,
        // Stringified so ICU renders the id verbatim rather than as a grouped number.
        untitledLot: String(
            formatMessage(['similarItemAlerts', 'untitledLot'], { values: { lotId: String(alert.lotId) } })
        ),
    });
    const lastAlert = hasNeverSent(alert.lastSent)
        ? String(formatMessage(['never']))
        : shortFormatDate(parseISO(alert.lastSent));
    const dateAdded = shortFormatDate(parseISO(alert.savedAt));

    const onDelete = useCallback(() => {
        dispatch(
            openSimilarItemAlertDeleteModal({
                daysSinceAdded: differenceInCalendarDays(new Date(), parseISO(alert.savedAt)),
                lotId: alert.lotId,
                lotTitle: title,
                savedItemId: alert.savedItemId,
            })
        );
    }, [alert.lotId, alert.savedAt, alert.savedItemId, dispatch, title]);

    return { dateAdded, imageUrl, lastAlert, onDelete, title };
};
